import { useState } from 'react';
import { faChevronDown, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AccordionHeader from './AccordionHeader';
import AccordionBody from './AccordionBody';
import { useOnboardingProvider } from '../../providers/OnboardingProvider';
import { useUserProvider } from '../../providers/UserProvider';
import { GET_STARTED, LAUNCHED, SAVED, SUBMITTED } from '../../lib/onboarding_config/config';

const CollapsedNode = ({
  title,
  hasDetails,
  isExpanded,
  isSubAccordion,
  status,
  hasEditLabel,
  isEditable = true,
  isCompleted = false,
  isDisabled = false,
  setIsExpanded
}) => {
  const { onboardingData } = useOnboardingProvider();
  const { user } = useUserProvider();

  const isShowEdit = () => {
    const preCondition = isEditable && !isExpanded && !isDisabled & !isSubAccordion;

    const { overall: { percentage: uploadContentPercentage } = {}, status: uploadContentStatus } = onboardingData?.progress?.content || {}

    switch(title) {
      case "App Design":
        return preCondition && (![SAVED, SUBMITTED, GET_STARTED].includes(onboardingData?.progress?.design?.status) || user?.isSuper)
      case "Essentials":
        return preCondition && (![SAVED, SUBMITTED, GET_STARTED].includes(onboardingData?.progress?.design?.status) || user?.isSuper)
      case "App Store Info":
        return preCondition && (![SAVED, SUBMITTED, GET_STARTED].includes(onboardingData?.progress?.store?.status) || user?.isSuper)
      case "Home/Profile":
        return preCondition && (![SAVED, SUBMITTED, GET_STARTED].includes(onboardingData?.progress?.home_profile?.status) || user?.isSuper)
      case "Build and Submit (admin)":
        return preCondition && ![SAVED, SUBMITTED, GET_STARTED, LAUNCHED].includes(onboardingData?.progress?.build_and_submit?.status)
      case "Upload Content":
        return preCondition && ((onboardingData?.progress?.content?.overall?.percentage < 100 && onboardingData?.progress?.content?.overall?.percentage > 0) || ![SAVED, SUBMITTED].includes(onboardingData?.progress?.content?.status) || user?.isSuper)
      default:
        return preCondition && (![SAVED, SUBMITTED, GET_STARTED].includes(onboardingData?.progress?.developer?.status) || user?.isSuper)
    }
  }

  return (
    <div className="w-full flex justify-between items-center">
      <span
        className={`line-clamp-2 text-xl font-bold ${isDisabled ? "text-darkGray" : ""} ${hasDetails && !isDisabled && isEditable ? "text-aeblue-link underline hover:no-underline text-underline-position-under" : ""} ${isSubAccordion ? 'text-2xl' : 'text-3xl'}`}
      >
        {title}
        {
          isShowEdit() ? (
            <button className='text-aeblue-default font-bold pl-2 underline hover:no-underline text-underline-position-under'>
              (edit)
            </button>
          ) : null
        }
      </span>
      <div className="flex items-center gap-3">
        <div>{status}</div>
        <div role="presentation" className={`cursor-pointer ${hasDetails && !isDisabled ? "visible" : "hidden"}`}>
          <FontAwesomeIcon
            icon={isSubAccordion ? faCaretDown : faChevronDown}
            size={isSubAccordion ? 'xl' : '2xl'}
            color="#2F91CF"
            className={`transition-all duration-500 ${
              isExpanded ? 'is-acc-open' : ''
            }`}
          />
        </div>
      </div>
    </div>
  );
};

const Accordion = ({ item, value, isSubAccordion, onChange, index, indexLength }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleChange = async (val) => {
    await onChange(item.id, val);
  };

  return (
    <AccordionHeader
      id={item.id}
      isExpandable={item?.has_details && !item?.is_disabled}
      isSubAccordion={isSubAccordion}
      isChecked={value}
      setIsChecked={handleChange}
      index={index}
      indexLength={indexLength}
      collapsedNode={
        <CollapsedNode
          title={item.label}
          hasDetails={item?.has_details ?? false}
          isExpanded={isExpanded}
          status={item.status}
          isSubAccordion={isSubAccordion}
          isCompleted={item.is_complete ?? false}
          isDisabled={item?.is_disabled ?? false}
          isEditable={item?.is_editable ?? true}
          hasEditLabel={item.hasEditLabel}
          setIsExpanded={setIsExpanded}
        />
      }
      expandedNode={
        <AccordionBody
          data={item}
          isSubAccordion={isSubAccordion}
          children={item.body}
          isExpandable={item?.has_details ?? false}
          index={index}
          indexLength={indexLength}
        />
      }
      isExpanded={isExpanded}
      isEditable={item?.is_editable ?? true}
      setIsExpanded={setIsExpanded}
      disabled={!item?.editable || item?.is_disabled}
    />
  );
};

export default Accordion;
